import React, { useState } from 'react';
import { Clock, Mic, MicOff, Video, VideoOff, Power, Users, MonitorUp } from 'lucide-react';
import ScreenShareModal from './ScreenShareModal';
import { useConnectionState, useLocalParticipant, useRemoteParticipants } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { toast } from 'react-toastify';

interface SessionHeaderProps {
    teamId: string;
    goal: string;
    isMuted: boolean;
    hasToken: boolean;
    onToggleMute: () => void;
    onEndCall: () => void;
    onToggleSidebar?: () => void;
    isSidebarOpen?: boolean;
}

const SessionHeader: React.FC<SessionHeaderProps> = ({
    teamId,
    goal,
    isMuted: reduxIsMuted,
    hasToken,
    onToggleMute,
    onEndCall,
    onToggleSidebar,
    isSidebarOpen
}) => {
    const connectionState = useConnectionState();
    const [isScreenShareModalOpen, setIsScreenShareModalOpen] = useState(false);
    const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
    const remoteParticipants = useRemoteParticipants();

    const isSomeoneElseSharing = remoteParticipants.some(p => p.isScreenShareEnabled);

    const showControls = hasToken || connectionState === ConnectionState.Connected || connectionState === ConnectionState.Reconnecting;

    const handleToggleMute = () => {
        if (localParticipant) {
            localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
        }
        if (onToggleMute) {
            onToggleMute();
        }
    };

    const handleToggleVideo = async () => {
        if (localParticipant) {
            try {
                await localParticipant.setCameraEnabled(!isCameraEnabled);
            } catch (error) {
                console.error("Could not toggle camera. Check permissions.", error);
                toast.error("Could not toggle camera. Please check your permissions.");
            }
        }
    };

    const handleStartScreenShare = async () => {
        if (localParticipant) {
            try {
                // If it is already sharing, toggle it off. Otherwise, it will trigger the browser picker.
                await localParticipant.setScreenShareEnabled(!isScreenShareEnabled);
            } catch (error) {
                console.error("Could not toggle screen share. Check permissions.", error);
            }
        }
    };

    const isMuted = localParticipant ? !isMicrophoneEnabled : reduxIsMuted;
    const isVideoOn = localParticipant ? isCameraEnabled : false;
    const isSharingScreen = localParticipant ? isScreenShareEnabled : false;

    return (
        <>
            <div className="bg-white border-b border-gray-200/60 p-4 sm:px-6 flex items-center justify-between shadow-sm z-20 relative">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-sm shrink-0">
                        <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-medium text-gray-900 mb-0.5 truncate">
                            Team Focus: {teamId}
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-600 font-light truncate">Goal: {goal}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    {/* Participants Toggle (Mobile Only) */}
                    <button
                        onClick={onToggleSidebar}
                        className={`sm:hidden p-2.5 rounded-full transition-colors ${isSidebarOpen ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                        title="Toggle Participants"
                    >
                        <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>

                    {showControls && (
                        <>
                            <button
                                onClick={() => {
                                    if (isSharingScreen) {
                                        handleStartScreenShare(); // Stop sharing directly
                                    } else if (isSomeoneElseSharing) {
                                        toast.error("You can't share screen when someone else is already sharing");
                                    } else {
                                        setIsScreenShareModalOpen(true); // Open Modal
                                    }
                                }}
                                className={`p-2.5 sm:p-3 rounded-full transition-colors ${!isSharingScreen
                                    ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm'
                                    }`}
                                title={isSharingScreen ? "Stop sharing screen" : "Share screen"}
                            >
                                <MonitorUp className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <button
                                onClick={handleToggleMute}
                                className={`p-2.5 sm:p-3 rounded-full transition-colors ${isMuted
                                    ? 'bg-red-500 hover:bg-red-600 text-white shadow-sm'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                title={isMuted ? "Unmute" : "Mute"}
                            >
                                {isMuted ? (
                                    <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />
                                ) : (
                                    <Mic className="w-4 h-4 sm:w-5 sm:h-5" />
                                )}
                            </button>
                            <button
                                onClick={handleToggleVideo}
                                className={`p-2.5 sm:p-3 rounded-full transition-colors ${!isVideoOn
                                    ? 'bg-red-500 hover:bg-red-600 text-white shadow-sm'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                title={isVideoOn ? "Turn off video" : "Turn on video"}
                            >
                                {isVideoOn ? (
                                    <Video className="w-4 h-4 sm:w-5 sm:h-5" />
                                ) : (
                                    <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />
                                )}
                            </button>
                        </>
                    )}

                    <button
                        onClick={onEndCall}
                        className="p-2.5 sm:p-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors shadow-sm text-white"
                        title="End Call"
                    >
                        <Power className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                </div>
            </div>

            <ScreenShareModal
                isOpen={isScreenShareModalOpen}
                onClose={() => setIsScreenShareModalOpen(false)}
                onStartShare={handleStartScreenShare}
            />
        </>
    );
};

export default SessionHeader;
